import React, { useMemo } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { InputNumber, Modal, Select } from 'antd'
import { useStore, useUnit } from 'effector-react'
import { EditWalletTransaction } from './types'
import { Currency } from '../../../../../views/widgets/walletCard/types'
import { $walletAllowedList } from '../GeneralTransactionModal/model'
import { editWalletTransaction, editWalletTransactionMounted } from './model'
import { useEffectNotifications } from '../useEffectNotifications'

type Props = {
  transaction: EditWalletTransaction
  allCurrencies: Currency[]
  isModalOpen: boolean
  onClose: () => void
}

type FormData = {
  walletId: string
  amount: number | null
  currency: string
  ourCommission: number | null
  ourCommissionCurrency: string
}

const Field: React.FC<{
  label: string
  required?: boolean
  error?: string
  children: React.ReactNode
  style?: React.CSSProperties
}> = ({ label, required, error, children, style }) => (
  <div className="mb-4" style={style}>
    <label className={`mb-1 block ${required ? 'ant-form-item-required' : ''}`}>
      {label}
    </label>
    {children}
    {error && (
      <div className="ant-form-item-explain-error mt-1 text-red-500 text-sm">
        {error}
      </div>
    )}
  </div>
)

export const EditWalletTransactionModal: React.FC<Props> = ({
  transaction,
  allCurrencies,
  isModalOpen,
  onClose,
}) => {
  const currencyOptions = useMemo(
    () => allCurrencies.map((c) => ({ value: c.code, label: c.code })),
    [allCurrencies],
  )

  const wallets = useUnit($walletAllowedList)
  const walletOptions = useMemo(
    () => wallets.map((w) => ({ value: String(w.id), label: w.name })),
    [wallets],
  )

  const { handleSubmit, control, reset } = useForm<FormData>({
    mode: 'onChange',
    defaultValues: {
      walletId: String(transaction.wallet),
      amount: transaction.amount,
      currency: transaction.currency,
      ourCommission: transaction.ourCommission,
      ourCommissionCurrency: transaction.ourCommissionCurrency,
    },
  })

  const isSubmitting = useUnit(editWalletTransaction.pending)

  const handleCancel = () => {
    reset()
    onClose()
  }

  const onSubmit = (data: FormData) => {
    editWalletTransactionMounted({
      transactionId: transaction.transactionId,
      type: transaction.type,
      wallet: Number(data.walletId),
      amount: data.amount ?? 0,
      currency: data.currency,
      ourCommission: data.ourCommission ?? 0,
      ourCommissionCurrency: data.ourCommissionCurrency ?? '',
    })
  }

  useEffectNotifications([
    {
      effect: editWalletTransaction,
      successMessage: 'Transaction successfully updated',
      errorMessage: 'Failed to update transaction',
      onSuccess: handleCancel,
    },
  ])

  return (
    <Modal
      title="Edit transaction"
      open={isModalOpen}
      onOk={handleSubmit(onSubmit)}
      onCancel={handleCancel}
      okText="Save"
      confirmLoading={isSubmitting}
      cancelButtonProps={{ disabled: isSubmitting }}
      destroyOnHidden
    >
      <form id="edit-wallet-transaction-form" onSubmit={handleSubmit(onSubmit)}>
        <Controller
          name="walletId"
          control={control}
          rules={{ required: 'Wallet is required' }}
          render={({ field, fieldState: { error } }) => (
            <Field label="Wallet" required error={error?.message}>
              <Select
                {...field}
                placeholder="Select wallet"
                showSearch
                optionFilterProp="label"
                options={walletOptions}
                style={{ width: '100%' }}
                status={error ? 'error' : undefined}
              />
            </Field>
          )}
        />

        <div style={{ display: 'flex', gap: 8 }}>
          <Controller
            name="amount"
            control={control}
            rules={{ required: 'Amount is required' }}
            render={({ field, fieldState: { error } }) => (
              <Field
                label="Amount"
                required
                error={error?.message}
                style={{ flex: 1 }}
              >
                <InputNumber
                  {...field}
                  placeholder="0"
                  style={{ width: '100%' }}
                  status={error ? 'error' : undefined}
                />
              </Field>
            )}
          />
          <Controller
            name="currency"
            control={control}
            rules={{ required: 'Currency is required' }}
            render={({ field, fieldState: { error } }) => (
              <Field
                label="Currency"
                required
                error={error?.message}
                style={{ width: 120 }}
              >
                <Select
                  {...field}
                  placeholder="Currency"
                  options={currencyOptions}
                  style={{ width: '100%' }}
                  status={error ? 'error' : undefined}
                />
              </Field>
            )}
          />
        </div>

        <div style={{ display: 'flex', gap: 8 }}>
          <Controller
            name="ourCommission"
            control={control}
            render={({ field, fieldState: { error } }) => (
              <Field
                label="Our commission"
                error={error?.message}
                style={{ flex: 1 }}
              >
                <InputNumber
                  {...field}
                  placeholder="0"
                  style={{ width: '100%' }}
                  status={error ? 'error' : undefined}
                />
              </Field>
            )}
          />
          <Controller
            name="ourCommissionCurrency"
            control={control}
            render={({ field, fieldState: { error } }) => (
              <Field
                label="Currency"
                error={error?.message}
                style={{ width: 120 }}
              >
                <Select
                  {...field}
                  placeholder="Currency"
                  allowClear
                  options={currencyOptions}
                  style={{ width: '100%' }}
                  status={error ? 'error' : undefined}
                />
              </Field>
            )}
          />
        </div>
      </form>
    </Modal>
  )
}
